import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import {
  DeviationType,
  getDeviationMessage,
} from "../../helpers/intakeHelpers";

type Props = {
  deviationType: DeviationType;
  show: boolean;
};

/** The app's arrow icon; points down for a reduced intake, up for an increased one. */
const ArrowIcon = ({ fill, flipped }: { fill: string; flipped: boolean }) => (
  <svg
    width="13"
    height="12"
    viewBox="0 0 13 12"
    fill="none"
    style={{ transform: flipped ? "rotate(180deg)" : "none" }}
  >
    <path
      d="M10.7803 6.96975C10.6397 6.82915 10.4489 6.75016 10.2501 6.75016C10.0512 6.75016 9.86046 6.82915 9.71981 6.96975L7.25006 9.4395V0.75C7.25006 0.551088 7.17104 0.360322 7.03039 0.21967C6.88974 0.0790176 6.69897 0 6.50006 0C6.30115 0 6.11038 0.0790176 5.96973 0.21967C5.82908 0.360322 5.75006 0.551088 5.75006 0.75V9.4395L3.28031 6.96975C3.13886 6.83313 2.94941 6.75754 2.75276 6.75924C2.55611 6.76095 2.368 6.83983 2.22895 6.97889C2.08989 7.11794 2.01101 7.30605 2.00931 7.5027C2.0076 7.69935 2.08319 7.8888 2.21981 8.03025L5.96981 11.7802C6.11046 11.9209 6.30119 11.9998 6.50006 11.9998C6.69893 11.9998 6.88967 11.9209 7.03031 11.7802L10.7803 8.03025C10.9209 7.8896 10.9999 7.69887 10.9999 7.5C10.9999 7.30113 10.9209 7.1104 10.7803 6.96975Z"
      fill={fill}
    />
  </svg>
);

/** The app's `IntakeDeviationMessage`: a light-blue card with an arrow badge, title and explanation. */
const IntakeDeviationMessage = ({ deviationType, show }: Props) => {
  const theme: Theme = useTheme();
  if (!show) return null;
  const content = getDeviationMessage(deviationType);

  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.titleRow}>
        <Box sx={styles.arrowBadge(theme)}>
          <ArrowIcon
            fill={theme.colorTokens.D[60]}
            flipped={deviationType === "increased"}
          />
        </Box>
        <Box sx={styles.title(theme)}>{content.title}</Box>
      </Box>
      <Box sx={styles.description(theme)}>{content.description}</Box>
    </Box>
  );
};

export default IntakeDeviationMessage;

const styles = {
  card: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.P[10],
    borderRadius: "12px",
    padding: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  arrowBadge: (theme: Theme) => ({
    width: "24px",
    height: "24px",
    borderRadius: "12px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(31, 34, 36, 0.1)",
  }),
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  // 12px body like the platform's other banners (lab-panel status, history).
  description: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
